import { Component, type ErrorInfo, type ReactNode } from 'react';
import { capturePostHogEvent } from '../lib/posthog';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Top-level error boundary. Captures React render-time crashes to PostHog as
 * `frontend_react_error` with component stack, and renders a minimal fallback
 * UI with a retry button.
 *
 * Per SIG-63: only emit route + error message + stacks. Never serialize
 * children props, state, or DOM contents — they may contain user-entered text
 * or article bodies.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    capturePostHogEvent('frontend_react_error', {
      error_message: error.message,
      error_stack: error.stack,
      component_stack: info.componentStack,
      route: window.location.pathname,
    });
  }

  private handleRetry = (): void => {
    this.setState({ hasError: false });
  };

  override render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.fallback !== undefined) {
      return this.props.fallback;
    }

    return (
      <div
        style={{
          padding: 32,
          textAlign: 'center',
          fontFamily: 'system-ui, sans-serif',
        }}
      >
        <h2 style={{ marginBottom: 16 }}>Something went wrong.</h2>
        <button
          type="button"
          onClick={this.handleRetry}
          style={{
            padding: '8px 16px',
            fontSize: 14,
            cursor: 'pointer',
          }}
        >
          Try again
        </button>
      </div>
    );
  }
}
